// Integer destinations let new input retarget an in-flight transition without
// calculating the next card from a partially scrolled viewport.
export function wrapIndex(index, count) {
  return count > 0 ? ((index % count) + count) % count : 0;
}

export function circularOffset(index, selected, count) {
  const offset = wrapIndex(index - selected, count);
  return offset > Math.floor(count / 2) ? offset - count : offset;
}

export function swipeDirection(dx, dy, duration, width) {
  if (Math.abs(dx) <= Math.abs(dy) * 1.2) return 0;
  const threshold = Math.min(60, Math.max(28, width * 0.12));
  const flick = Math.abs(dx) >= 18 && Math.abs(dx) / Math.max(1, duration) >= 0.4;
  return Math.abs(dx) >= threshold || flick ? (dx < 0 ? 1 : -1) : 0;
}

const ElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

export class MiniCubezInstagramCarousel extends ElementBase {
  connectedCallback() {
    if (this.events) return;
    this.cards = [...this.querySelectorAll('[data-post-card]')];
    this.stage = this.querySelector('[data-post-stage]');
    this.status = this.querySelector('[data-post-status]');
    if (!this.stage || !this.cards.length) return;
    this.index = wrapIndex(this.index || 0, this.cards.length);
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.entryGeneration = (this.entryGeneration || 0) + 1;
    this.galleries = this.cards.map(card => ({
      card,
      element: card.querySelector('[data-post-media]'),
      photos: [...card.querySelectorAll('[data-post-photo]')],
      dots: [...card.querySelectorAll('[data-photo-dot]')],
      index: Number(card.dataset.photoIndex) || 0
    }));

    this.addEventListener('click', event => {
      if (this.swallowClick && event.detail > 0) {
        this.swallowClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const postButton = event.target.closest('[data-post-direction]');
      if (postButton && this.contains(postButton)) {
        this.selectPost(this.index + Number(postButton.dataset.postDirection));
        return;
      }
      const photoButton = event.target.closest('[data-photo-direction]');
      if (photoButton && this.contains(photoButton)) {
        const gallery = this.galleries[this.index];
        this.selectPhoto(gallery, gallery.index + Number(photoButton.dataset.photoDirection));
      }
    }, { ...options, capture: true });

    this.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (event.target.closest('input, textarea, select')) return;
      this.cancelEntry();
      const gallery = event.target.closest('[data-post-media]') && this.galleries[this.index];
      const current = gallery ? gallery.index : this.index;
      const count = gallery ? gallery.photos.length : this.cards.length;
      const target = event.key === 'Home' ? 0 : event.key === 'End' ? count - 1 : current + (event.key === 'ArrowRight' ? 1 : -1);
      event.preventDefault();
      if (gallery) this.selectPhoto(gallery, target);
      else this.selectPost(target);
    }, options);

    this.addEventListener('pointerdown', event => this.startGesture(event), options);
    this.addEventListener('pointermove', event => this.moveGesture(event), { ...options, passive: false });
    this.addEventListener('pointerup', event => this.endGesture(event), options);
    this.addEventListener('pointercancel', event => {
      if (event.pointerId === this.gesture?.id) this.clearGesture();
    }, options);
    this.addEventListener('lostpointercapture', event => {
      // Touch starts with implicit capture on the child. Its capture-loss event
      // bubbles when a horizontal swipe transfers capture to this carousel.
      if (event.target === this && event.pointerId === this.gesture?.id) this.clearGesture();
    }, options);
    this.addEventListener('focusin', () => this.cancelEntry(), options);
    this.addEventListener('dragstart', event => {
      if (event.target.closest('[data-post-card]')) event.preventDefault();
    }, options);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.cancelEntry();
    }, options);
    document.addEventListener('shopify:block:select', event => {
      const index = this.cards.findIndex(card => card.dataset.blockId === event.detail?.blockId);
      if (index >= 0) this.selectPost(index);
    }, options);
    this.motion.addEventListener('change', () => {
      this.clearGesture();
      if (this.motion.matches) this.cancelEntry();
      this.measure();
    }, options);

    this.galleries.forEach(gallery => {
      gallery.photos.forEach(photo => {
        photo.hidden = false;
        const image = photo.querySelector('img');
        if (!image) return;
        image.addEventListener('error', () => this.restoreOriginal(image), options);
        if (image.complete && image.getAttribute('src') && !image.naturalWidth) this.restoreOriginal(image);
      });
      this.renderPhoto(gallery);
    });
    this.dataset.enhanced = '';
    this.render();
    this.resize = new ResizeObserver(() => this.scheduleMeasure());
    this.cards.forEach(card => this.resize.observe(card.querySelector('[data-post-body]')));
    this.resize.observe(this.stage);
    this.querySelectorAll('[data-post-direction]').forEach(button => { button.disabled = this.cards.length < 2; });

    if ('IntersectionObserver' in window) {
      this.preload = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.preload.disconnect();
          this.warmNeighbors();
        }
      }, { rootMargin: '500px' });
      this.preload.observe(this.stage);
      this.entryObserver = new IntersectionObserver(entries => {
        this.inView = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.4);
        if (!this.inView) {
          this.invalidateEntry();
        } else if (!this.entrySeen && !this.entryTimer && !this.entryPending && !this.motion.matches && !window.Shopify?.designMode && this.cards.length > 1) {
          this.entryTimer = setTimeout(() => this.advanceOnEntry(), 950);
        }
      }, { threshold: [0, 0.4], rootMargin: '-90px 0px -30px 0px' });
      this.entryObserver.observe(this.stage);
    }
    requestAnimationFrame(() => {
      if (this.isConnected) this.dataset.animated = '';
    });
  }

  disconnectedCallback() {
    this.events?.abort();
    this.events = null;
    this.resize?.disconnect();
    this.preload?.disconnect();
    this.entryObserver?.disconnect();
    this.invalidateEntry();
    cancelAnimationFrame(this.measureFrame);
    this.measureFrame = null;
    this.clearGesture();
  }

  selectPost(index, manual = true) {
    if (manual) this.cancelEntry();
    this.clearGesture();
    const next = wrapIndex(index, this.cards.length);
    if (next === this.index) return;
    if (this.cards[this.index].contains(document.activeElement)) this.stage.focus({ preventScroll: true });
    this.index = next;
    this.render(manual);
    this.warmNeighbors();
  }

  render(announce = false) {
    this.dataset.activeIndex = String(this.index);
    this.cards.forEach((card, index) => {
      const offset = circularOffset(index, this.index, this.cards.length);
      card.dataset.postOffset = String(Math.max(-2, Math.min(2, offset)));
      card.inert = index !== this.index;
      card.setAttribute('aria-hidden', String(index !== this.index));
    });
    if (this.status) {
      this.status.setAttribute('aria-live', announce ? 'polite' : 'off');
      this.status.textContent = this.status.dataset.template.replace('__CURRENT__', String(this.index + 1));
    }
    this.measure();
  }

  scheduleMeasure() {
    if (this.measureFrame) return;
    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = null;
      if (this.isConnected) this.measure();
    });
  }

  measure() {
    const body = this.cards[this.index]?.querySelector('[data-post-body]');
    if (!body || !this.stage.clientWidth) return;
    // offsetHeight is unaffected by the side cards' 3D transforms.
    const height = Math.ceil(body.offsetHeight) + 24;
    if (this.stage.style.height !== height + 'px') this.stage.style.height = height + 'px';
  }

  selectPhoto(gallery, index) {
    this.cancelEntry();
    this.clearGesture();
    gallery.index = Math.max(0, Math.min(gallery.photos.length - 1, index));
    this.renderPhoto(gallery);
    this.warmPhoto(gallery, gallery.index + 1);
  }

  renderPhoto(gallery) {
    gallery.card.dataset.photoIndex = String(gallery.index);
    gallery.photos.forEach((photo, index) => {
      photo.style.setProperty('--mvr-photo-offset', Math.sign(index - gallery.index));
      photo.setAttribute('aria-hidden', String(index !== gallery.index));
    });
    gallery.dots?.forEach((dot, index) => dot.toggleAttribute('data-active', index === gallery.index));
    gallery.element?.querySelectorAll('[data-photo-direction]').forEach(button => {
      const disabled = Number(button.dataset.photoDirection) < 0 ? gallery.index === 0 : gallery.index === gallery.photos.length - 1;
      if (disabled && document.activeElement === button) gallery.element.focus({ preventScroll: true });
      button.disabled = disabled;
    });
    const counter = gallery.element?.querySelector('[data-photo-counter]');
    if (counter) counter.textContent = (gallery.index + 1) + ' / ' + gallery.photos.length;
    this.warmPhoto(gallery, gallery.index);
  }

  warmPhoto(gallery, index) {
    const image = gallery?.photos[index]?.querySelector('img[data-mvr-src]');
    if (!image) return;
    const source = image.dataset.mvrSrc;
    delete image.dataset.mvrSrc;
    // Intent-driven preloading must also fetch photos outside the clipped frame.
    image.loading = 'eager';
    if (image.dataset.mvrSrcset) image.srcset = image.dataset.mvrSrcset;
    image.src = source;
  }

  warmNeighbors() {
    for (const offset of [-1, 0, 1]) {
      const gallery = this.galleries[wrapIndex(this.index + offset, this.cards.length)];
      this.warmPhoto(gallery, gallery.index);
    }
    const active = this.galleries[this.index];
    active.photos.forEach((photo, index) => this.warmPhoto(active, index));
  }

  restoreOriginal(image) {
    if (!image.dataset.mvrOriginal || image.dataset.originalRestored) return;
    image.dataset.originalRestored = 'true';
    image.removeAttribute('srcset');
    delete image.dataset.mvrSrcset;
    image.src = image.dataset.mvrOriginal;
  }

  cancelEntry() {
    this.entrySeen = true;
    this.invalidateEntry();
    this.entryObserver?.disconnect();
  }

  invalidateEntry() {
    this.entryGeneration = (this.entryGeneration || 0) + 1;
    this.entryPending = false;
    clearTimeout(this.entryTimer);
    this.entryTimer = null;
  }

  async advanceOnEntry() {
    this.entryTimer = null;
    if (this.entrySeen || !this.inView || this.motion.matches || document.hidden || !this.isConnected) return;
    const next = wrapIndex(this.index + 1, this.cards.length);
    const gallery = this.galleries[next];
    const attempt = this.entryGeneration;
    this.entryPending = true;
    this.warmPhoto(gallery, gallery.index);
    try { await gallery.photos[gallery.index].querySelector('img').decode(); } catch { /* The original-post link remains available if a photo is unavailable. */ }
    if (attempt !== this.entryGeneration) return;
    this.entryPending = false;
    if (this.entrySeen || !this.inView || this.motion.matches || document.hidden || !this.isConnected) return;
    this.entrySeen = true;
    this.entryObserver?.disconnect();
    this.selectPost(next, false);
  }

  startGesture(event) {
    this.cancelEntry();
    this.swallowClick = false;
    if (!event.isPrimary || event.button !== 0) { this.clearGesture(); return; }
    if (!this.contains(event.target) || event.target.closest('button, input, select, textarea')) return;
    const media = event.target.closest('[data-post-media]');
    const gallery = media ? this.galleries[this.index] : null;
    if (media && media !== gallery.element) return;
    this.gesture = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      time: performance.now(),
      axis: null,
      gallery,
      width: (gallery?.element || this.cards[this.index]).clientWidth
    };
  }

  moveGesture(event) {
    const gesture = this.gesture;
    if (!gesture || gesture.id !== event.pointerId) return;
    const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
    if (!gesture.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 8) {
      gesture.axis = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'horizontal' : 'vertical';
      if (gesture.axis === 'horizontal') this.setPointerCapture(event.pointerId);
    }
    if (gesture.axis !== 'horizontal') return;
    event.preventDefault();
    const surface = gesture.gallery?.element || this;
    const limit = gesture.gallery ? gesture.width * 0.35 : Math.min(65, gesture.width * 0.25);
    surface.dataset.dragging = '';
    surface.style.setProperty(gesture.gallery ? '--mvr-photo-drag' : '--mvr-post-drag', Math.max(-limit, Math.min(limit, dx)) + 'px');
  }

  endGesture(event) {
    const gesture = this.gesture;
    if (!gesture || gesture.id !== event.pointerId) return;
    const direction = gesture.axis === 'horizontal' ? swipeDirection(event.clientX - gesture.x, event.clientY - gesture.y, performance.now() - gesture.time, gesture.width) : 0;
    if (gesture.axis === 'horizontal') this.swallowClick = true;
    this.clearGesture();
    if (!direction) return;
    if (gesture.gallery) this.selectPhoto(gesture.gallery, gesture.gallery.index + direction);
    else this.selectPost(this.index + direction);
  }

  clearGesture() {
    const id = this.gesture?.id;
    this.gesture = null;
    delete this.dataset.dragging;
    this.style.removeProperty('--mvr-post-drag');
    this.galleries?.forEach(gallery => {
      if (!gallery.element) return;
      delete gallery.element.dataset.dragging;
      gallery.element.style.removeProperty('--mvr-photo-drag');
    });
    if (id !== undefined && this.hasPointerCapture?.(id)) this.releasePointerCapture(id);
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('minicubez-instagram-carousel')) {
  customElements.define('minicubez-instagram-carousel', MiniCubezInstagramCarousel);
}
